import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PriceDtoV2, PromotionCodeDtoV2 } from '@nusign/picflow-backend-types';
import { catchError, map, Observable, of } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { Product, RecurringInterval } from '../types/product.type';
import { PromotionWithCode } from '../types/promotion';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { TransferStateService } from './transfer-state.service';

@Injectable({
  providedIn: 'root',
})
export class PromotionService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);
  private transferState = inject(TransferStateService);

  public checkCode(code: string): Observable<PromotionWithCode> {
    return this.transferState.withStateTransferObservable('promo_' + code, () => {
      return this.loader.get('promo_' + code, () => this.checkCodeInternal(code));
    });
  }

  public appliesToPrice(promo: PromotionWithCode, price: PriceDtoV2) {
    const billingFrequency = price.recurring_interval;
    if (billingFrequency === 'month' && promo.duration === 'once' && promo.amount_off) return false;
    if (!promo.applies_to?.length) {
      return price.product_type !== 'free'; // No appliesTo means it applies to every product EXCEPT FOR FREE
    }
    return promo.applies_to.includes(price.product_type as PromotionWithCode['applies_to'][number]);
  }

  public appliesToProduct(promo: PromotionWithCode, product: Product, billingFrequency?: RecurringInterval) {
    if (billingFrequency === 'month' && promo.duration === 'once' && promo.amount_off) return false;
    if (!promo.applies_to?.length) return !product.isFree; // No appliesTo means it applies to every product EXCEPT FOR FREE
    return promo.applies_to.includes(product.id as PromotionWithCode['applies_to'][number]);
  }

  public appliesToAddon(promo: PromotionWithCode, addon: PriceDtoV2) {
    if (promo.amount_off) return false;
    if (!promo.applies_to?.length) return true; // No appliesTo means it applies to every addon
    return promo.applies_to.includes(addon.product_type as PromotionWithCode['applies_to'][number]);
  }

  private checkCodeInternal(code: string): Observable<PromotionWithCode> {
    return this.http
      .get<PromotionCodeDtoV2>(`${this.config.apiEndpoint}/billing/subscription/promotion_codes/${code}`)
      .pipe(
        map(promo => {
          return {
            ...promo,
            code,
          };
        }),
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (![404, 500].includes(err.status)) throw err;
          return of(undefined);
        })
      );
  }
}
